import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from "@angular/core";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { HttpClient } from "@angular/common/http";
import { GlobalSpinnerService } from "@app/services/misc-services/global-spinner.service";
import { DocumentModel } from "@app/model/document.model";
import { MatDialog } from "@angular/material/dialog";
import { DocumentDialogComponent } from "@app/pages/documents/document-dialog/document-dialog.component";
import { LoginService } from "@app/login/login.service";
import { BehaviorSubject } from "rxjs";
import { PortalUser } from "@app/model/portal-user.model";
import { AlertsService } from "@app/services/misc-services/alerts.service";
import { Base64ConversionService } from "@app/services/misc-services/base64-conversion.service";
import { SearchFilterService } from "@app/services/misc-services/search-filter.service";
import { DocumentDeleteComponent } from "@app/pages/documents/document-delete/document-delete.component";

@Component({
  selector: "flex-documents",
  templateUrl: "./documents.component.html",
  styleUrls: ["./documents.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentsComponent implements OnInit {
  activeUser: BehaviorSubject<PortalUser> = this.activeUserService.getActiveUserSubject();
  documents: MatTableDataSource<any> = new MatTableDataSource<any>([]);
  previewDocument: DocumentModel = null;
  //Uploaded document variables
  uploadTitle: string = "";
  uploadFile: File;
  uploadFileAsB64: string = "";
  uploadDescription: string = "";
  uploadCategory: string = "";
  uploadCatTitle: string = "";
  uploadCatShorthand: string = "";
  uploadDocs: boolean = false;
  //Document types for filtering & assigning category for uploads
  documentType: DocType[] = [
    { label: "CMC Administration Info", value: "CMCAdmin" },
    { label: "Correspondence with Clients", value: "Correspdence" },
    { label: "Due Diligence Documents for FLEX", value: "Duediligence" },
    { label: "Due Diligence - Third Party Vendors", value: "DueDilViaWest" },
    { label: "FLEX Documentation", value: "FlexDocs" },
    { label: "Hardware Support", value: "hardwareFiles" },
    { label: "Internet Banking", value: "InternetBanking" },
    { label: "Network Security", value: "netSecurity" },
    { label: "Training Events", value: "Training Events" }
  ];
  lastFilter: DocType;
  columns: string[] = ["download", "title", "description", "fileSize", "uploadDate", "fileName", "createdBy", "delete"];
  //Need for date display pipe
  dateColumn: string[] = ["Date"];
  searchValue: string = "";
  btnGroup: string = "";
  deleteBtn: boolean;
  hoveredRow: number;
  pageSizeOptions: number[] = [];

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  //Upload precheck
  rowHovered: boolean;

  //Post for uploading, gets called from uploadDocument function

  constructor(private http: HttpClient,
              private globalSpinner: GlobalSpinnerService,
              public documentPreview: MatDialog,
              private activeUserService: LoginService,
              public alertDialog: AlertsService,
              public b64: Base64ConversionService,
              private documentFilter: SearchFilterService,
              public alert: AlertsService,
              public deleteDialog: MatDialog) {
  }

  ngOnInit(): void {
    this.getDocuments();
    this.deleteBtn = false;
    this.pageSizeOptions = [15, 30, 50, this.documents.data.length];
  }

  //Get all documents and set to MatTableDataSource
  getDocuments() {
    this.globalSpinner.addEvent("loadingDocuments", "Loading Documents");
    this.clearFilter();
    this.http.get(`/api/secured/documents/allFiles`).subscribe((document: DocumentModel[]) => {
      this.documents.data = document;
      this.pageSizeOptions = [document.length];
      this.documents.sort = this.sort;
      this.documents.paginator = this.paginator;
      this.globalSpinner.removeEvent("loadingDocuments");
    });
  }

  //Opens document in DocumentDialogComponent
  viewDocument(selectedDocument: any) {
    this.http.get(`/api/secured/documents/previewFile/${selectedDocument.filename}`).subscribe((document: DocumentModel) => {
      const dialogRef = this.documentPreview.open(DocumentDialogComponent, {
        maxHeight: "750px",
        maxWidth: "1200px",
        data: {
          fileSize: document.fileSize,
          id: document.id,
          title: document.title,
          createdBy: document.createdBy,
          description: document.description,
          filename: document.filename,
          categoriesTitle: document.categoriesTitle,
          categories: document.categories,
          downloads: document.downloads,
          uploadDate: document.uploadDate,
          filedata: document.filedata
        }
      });
      dialogRef.afterClosed().subscribe(() => {
        this.getDocuments();
      });
    });
  }

  //Get request for downloading document
  downloadDocument(downloadDoc) {
    this.http.get(`/api/secured/documents/downloadFile/${downloadDoc.filename}`).subscribe(
      (response: DocumentModel) => {
        this.b64.downloadB64File(response.filedata, response.filename);
      });
  }

  //Toggle upload menu and functions for uploading new docs
  toggleUpload() {
    this.uploadDocs = !this.uploadDocs;
    if (this.uploadDocs === false) {
      this.uploadFile = null;
      this.uploadTitle = '';
      this.uploadDescription = '';
      this.uploadTitle = '';
      this.uploadCategory = '';
      this.uploadCatTitle = '';
      this.uploadCatShorthand = '';
    }
  }

  fileSelected(event) {
    if (event.target.files.length === 0) {
      this.uploadFile = null;
      return;
    } else {
      this.uploadFile = event.target.files;
      const _MIME = this.b64.b64Type(this.uploadFile[0]);
      this.b64.fileToBase64(this.uploadFile[0]).subscribe((data) => {
        this.uploadFileAsB64 = _MIME + data;
      });
    }
  }

  setUploadCategory(catTitle: string, catShorthand: string) {
    this.uploadCategory = catShorthand;
    this.uploadCatTitle = catTitle;
    this.uploadCatShorthand = catShorthand;
  }

  uploadDocument() {
    if (!this.uploadTitle) {
      this.alertDialog.getError("Missing Title", "X");
    } else {
      if (this.uploadCategory === "") {
        this.uploadCategory = "FLEX Documentation";
        this.uploadCatTitle = "FLEX Documentation";
        this.uploadCatShorthand = "FlexDocs";
      }
      this.uploadDocumentSubmit();
    }
  }

  uploadDocumentSubmit() {
    this.globalSpinner.addEvent("uploadDocument", "Uploading Document");
    this.http.post(`/api/secured/documents/uploadFile`, {
      fileName: this.uploadFile[0].name,
      file: this.uploadFileAsB64,
      categories: this.uploadCategory,
      title: this.uploadTitle,
      description: this.uploadDescription,
      fileSize: this.uploadFile[0].size,
      user: this.activeUser.value.username,
      catTitle: this.uploadCatTitle,
      catShorthand: this.uploadCatShorthand
    }).subscribe(() => {
        this.globalSpinner.removeEvent("uploadDocument");
        this.alertDialog.getAlert("Thank you, your document has been uploaded.", "X");
        this.toggleUpload();
        this.getDocuments();
      },
      () => {
        this.globalSpinner.removeEvent("uploadDocument");
        this.alertDialog.getError("Unable to upload, please try again.", "X");
      });
  }

  inputSearch(filterValue) {
    this.documentFilter.search(filterValue, this.documents);
  }

  filterDocuments(doctype: DocType) {
    this.documents.filterPredicate = (data: DocumentModel, filter: string) => {
      return data.categories === filter;
    };
    this.lastFilter = doctype;
    this.documents.filter = doctype.value;
  }

  clearFilter() {
    this.searchValue = "";
    this.btnGroup = "default";
    this.documents.filter = "";
    this.documentFilter.search("", this.documents);
  }

  confirmDelete(document) {
    const dialogRef = this.deleteDialog.open(DocumentDeleteComponent, {});
    dialogRef.afterClosed().subscribe((response) => {
      if (response === true) {
        this.deleteDocument(document.id);
      }
    });
  }

  deleteDocument(id: number) {
    this.http.get(`/api/secured/documents/deleteFile/${id}`).subscribe(() => {
        this.alert.getAlert("Document deleted", "X");
        this.getDocuments();
      },
      () => {
        this.alert.getError("Unable to delete, please try again.", "X");
      });
  }

  showDelete(id) {
    this.hoveredRow = id;
  }

  canDelete() {
    return this.activeUser.value.superAdmin;
  }
}

export class DocType {
  label: string;
  value: string;
}
